import { useCallback, useRef, useState } from 'react';
import { Info, BookOpen } from 'lucide-react';
import PortalPopover from './PortalPopover';
import { WISSEN_HINWEISE, wissenUrl } from '../../data/wissenHinweise';
import { trackEvent } from '../../lib/analytics';

// (i)-Hinweis: kleiner Knopf, Klick öffnet ein Popover mit Kurzerklärung und
// Link auf den passenden Wissensartikel (neuer Tab, damit die offene Kette
// bleibt). Texte und Links stehen in src/data/wissenHinweise.js.
// Abgrenzung zur Tour: die Tour zeigt WO etwas ist, das (i) erklärt WAS.

const TON = {
  hell: 'text-ink-4 hover:text-brand',
  dunkel: 'text-white/55 hover:text-white',
};

const linkFuer = (id, slug) => wissenUrl(slug, { medium: 'hinweis', campaign: `hinweis-${id}` });

export default function WissenHinweis({ id, ton = 'hell' }) {
  const [open, setOpen] = useState(false);
  const knopfRef = useRef(null);
  const innenRef = useRef(null);

  // Fokus nur zurückholen, wenn er im Popover lag (Esc aus einem Link heraus).
  // Beim Klick außerhalb setzt der Browser den Fokus danach selbst auf das Ziel.
  const schliessen = useCallback(() => {
    const imPopover = innenRef.current?.contains(document.activeElement);
    setOpen(false);
    if (imPopover) knopfRef.current?.focus();
  }, []);

  // Beim Öffnen den Fokus ins Popover setzen: es hängt am Ende von <body>,
  // ohne das erreichte Tab die Links erst nach der ganzen App.
  const innenMontiert = useCallback((el) => {
    innenRef.current = el;
    el?.focus({ preventScroll: true });
  }, []);

  const h = WISSEN_HINWEISE[id];
  if (!h?.titel) {
    if (import.meta.env.DEV) console.warn(`WissenHinweis: unbekannte id „${id}“`);
    return null;
  }

  return (
    <>
      <button
        ref={knopfRef}
        type="button"
        aria-label={`Erklärung: ${h.titel}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={(e) => {
          e.stopPropagation();
          if (!open) trackEvent('Wissen Hint Opened', { id });
          setOpen((o) => !o);
        }}
        className={`inline-flex items-center justify-center rounded-full transition-colors ${TON[ton] ?? TON.hell}`}
        style={{ width: 16, height: 16, flexShrink: 0, padding: 0, background: 'none', border: 0, cursor: 'pointer' }}
      >
        <Info size={12} aria-hidden="true" />
      </button>
      <PortalPopover anchorRef={knopfRef} open={open} onClose={schliessen} minWidth={260}>
        <div
          ref={innenMontiert}
          tabIndex={-1}
          style={{ outline: 'none', maxWidth: 320, padding: '12px 14px', textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}
        >
          <div className="text-ink" style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 6 }}>{h.titel}</div>
          <p className="text-ink-2" style={{ fontSize: 12, lineHeight: 1.5, margin: 0 }}>{h.text}</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 10 }}>
            {h.links.map((l) => (
              <a
                key={l.slug}
                href={linkFuer(id, l.slug)}
                target="_blank"
                rel="noopener"
                className="text-brand hover:underline"
                style={{ fontSize: 12, fontWeight: 600 }}
              >
                {`Mehr im Artikel: ${l.label} →`}
              </a>
            ))}
          </div>
        </div>
      </PortalPopover>
    </>
  );
}

// Inline-Link für bestehende Hinweistexte (z. B. Kasten „RSS-Annahmen“).
export function WissenArtikelLink({ id }) {
  const l = WISSEN_HINWEISE[id]?.links?.[0];
  if (!l) return null;
  return (
    <a href={linkFuer(id, l.slug)} target="_blank" rel="noopener" className="text-brand hover:underline" style={{ fontWeight: 600 }}>
      Mehr im Artikel →
    </a>
  );
}

// Menüeintrag „Wissen“ im Mehr-Menü (AppHeader), gleiche Optik wie „FAQ“.
export function WissenMenuLink({ onClick }) {
  return (
    <a
      href={wissenUrl()}
      target="_blank"
      rel="noopener"
      onClick={onClick}
      role="menuitem"
      className="flex items-center gap-3 px-4 py-2 text-xs text-ink-2 hover:bg-surface-2"
    >
      <BookOpen size={14} aria-hidden="true" />
      Wissen
    </a>
  );
}
